import { Grid, Drawer, Divider, Button, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { SettingsInformation } from "../../utils/account.types";
import CardData from "../CardData/CardData";
import SettingDialogForm from "./SettingDialogForm";
import { fetchAccountById, updateAccount, fetchBuyerOrSupplierByName } from "../../store/accountThunk";

const Settings: FC = () => {
  const dispatch = useAppDispatch();
  const [accountBasicInf, setaccountBasicInf] = useState<SettingsInformation>();
  const [open, setOpen] = useState(false);

  // Cash sale account state
  const [options, setOptions] = useState<any[]>([]);
  const [selectedCashSale, setSelectedCashSale] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [savingCashSale, setSavingCashSale] = useState(false);

  const accountDetails = useAppSelector(state => state.account.accountDetails);

  const cardClicked = () => {
    handleOpen();
  }

  const getData = (data: any): SettingsInformation => {
    const { subscription, verifiedStatus } = data;
    return {
      'subscription': { value: subscription?.plan, title: 'Plan' },
      'verifiedStatus': { value: verifiedStatus === 'false' ? 'No' : 'Yes', title: 'Verified Status' },
    };
  };

  const getSettingsData = () => {
    const data = [];
    for (const key in accountBasicInf) {
      data.push(<CardData key={key} card={accountBasicInf[key as keyof SettingsInformation]} />)
    }
    return data;
  }

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onSubmit = async (data: any) => {
    try {
      const responseData: any = await dispatch(updateAccount({
        id: accountDetails._id,
        accountPayload: data,
      }));

      if (responseData && responseData?.status === 200) {
        dispatch(fetchAccountById(accountDetails._id));
        handleClose();
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  // Debounce search input
  useEffect(() => {
    setIsSearching(true);
    const timerId = setTimeout(() => {
      setDebouncedTerm(searchTerm);
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  // Fetch buyers when debounced term changes
  useEffect(() => {
    if (!accountDetails?._id) return;
    setIsSearching(true);
    const fetchData = async () => {
      try {
        const queryString = `name=${debouncedTerm}&&searchFor=buyer`;
        const data: any = await dispatch(fetchBuyerOrSupplierByName(queryString)).unwrap();
        if (data) {
          const filtered = data.accounts.filter((item: any) => item._id !== accountDetails._id);
          setOptions(debouncedTerm ? filtered : filtered.slice(0, 5));
        }
      } catch (error) {
        console.error("Error fetching buyers:", error);
      }
      setIsSearching(false);
    };
    fetchData();
  }, [debouncedTerm]);

  // Handle buyer selection
  const handleCashSaleChange = async (_event: any, value: any) => {
    setSelectedCashSale(value);
    setSavingCashSale(true);
    try {
      const formData = new FormData();
      formData.append('defaultCashSaleAccount', value?._id || '');
      const responseData: any = await dispatch(updateAccount({
        id: accountDetails._id,
        accountPayload: formData as any,
      }));
      if (responseData && responseData?.status === 200) {
        dispatch(fetchAccountById(accountDetails._id));
      }
    } catch (error: any) {
      console.log(error);
    } finally {
      setSavingCashSale(false);
    }
  };

  // Sync selected value from account details
  useEffect(() => {
    let active = true;
    const settingsInfo = () => {
      try {
        if (active) {
          Object.keys(accountDetails).length && setaccountBasicInf(getData(accountDetails));
          if (accountDetails.defaultCashSaleAccount && typeof accountDetails.defaultCashSaleAccount === 'object') {
            setSelectedCashSale(accountDetails.defaultCashSaleAccount);
          } else {
            setSelectedCashSale(null);
          }
        }
      } catch (error: any) {
        console.log('Setting catch block called');
      }
    };
    settingsInfo();
    return () => { active = false; };
  }, [accountDetails]);

  return (
    <>
      <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10 }}>
        <Button
          size='small'
          variant="contained"
          style={{
            float: "right",
            color: '#488dff',
            backgroundColor: '#fff',
            border: '0.5px solid #488dff',
            height: 25, borderRadius: 7
          }}
          onClick={cardClicked}
        >Change
        </Button>
        <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>Settings</h5>
        <Divider sx={{ mt: 0.5, mb: 1 }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13 }}>{accountBasicInf?.subscription.title}</h5>
            <h5 style={{ fontWeight: 'normal', color: '#000', fontSize: 14, marginBottom: 3 }}>{accountBasicInf?.subscription.value}</h5>
          </Grid>
          <Grid item xs={6}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13 }}>{accountBasicInf?.verifiedStatus.title}</h5>
            <h5 style={{ fontWeight: 'normal', color: '#000', fontSize: 14, marginBottom: 3 }}>{accountBasicInf?.verifiedStatus.value}</h5>
          </Grid>
        </Grid>
        <Drawer
          anchor="right"
          open={open}
        >
          {<SettingDialogForm title='Update Account' open={open} data={accountDetails} onClose={handleClose} onSubmit={onSubmit} />}
        </Drawer>
      </div>

      <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 10, marginTop: 15 }}>
        <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>Default Cash Sale Account</h5>
        <Divider sx={{ mt: 0.5, mb: 1.5 }} />
        <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 6 }}>
          Select a buyer account to use as the default for cash sales
        </h5>
        <div style={{ display: 'flex', alignItems: 'center', maxWidth: 400 }}>
          <Autocomplete
            sx={{ width: '100%' }}
            value={selectedCashSale}
            onChange={handleCashSaleChange}
            onInputChange={(_e, value) => setSearchTerm(value)}
            options={options}
            getOptionLabel={(option: any) => option.name || ''}
            isOptionEqualToValue={(option: any, value: any) => option._id === value?._id}
            loading={isSearching}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder='Type here to search Buyer'
                className='c-text-field'
                variant="standard"
                size='small'
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: (
                    <>
                      {isSearching || savingCashSale ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option: any) => (
              <li {...props} key={option._id}>
                {option.name || option.legalName || option._id}
              </li>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default Settings;
